import React from "react";
import { Link } from "react-router-dom";
import { WarningCircle, ArrowsClockwise, House } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

/**
 * Orixa Invest — top-level error boundary.
 *
 * Catches uncaught render-tree errors so a single page crash never
 * blanks the whole application. Preserves the user's session, presents
 * a brand-aligned recovery screen with three options:
 *   1) Try again (clears the error, re-renders the children),
 *   2) Hard reload (cache-busted),
 *   3) Go to dashboard.
 *
 * Usage:
 *   <ErrorBoundary scope="Workspace detail">
 *     <WorkspaceDetail />
 *   </ErrorBoundary>
 *
 * Pass `scope` for analytics / logs. Pass `fallback` to render a custom UI.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const scope = this.props.scope || "Unknown";
    // Defensive logging — never throw from here
    try {
      console.error(`[Orixa] ErrorBoundary[${scope}] caught:`, error, info);
    } catch (_) {
      /* swallow — logging must never crash */
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHardReload = () => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("_bust", String(Date.now()));
      window.location.replace(u.toString());
    } catch (_) {
      window.location.reload();
    }
  };
  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error, this.handleRetry);

    const errMsg = (this.state.error && (this.state.error.message || String(this.state.error))) || "Unknown error";

    return (
      <div
        role="alert"
        aria-live="assertive"
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-background text-foreground grid place-items-center px-6 py-12"
      >
        <div className="max-w-xl w-full">
          <div className="mb-10">
            <Logo variant="wordmark" size={36} as="div" />
          </div>
          <div className="inline-flex items-center gap-2 text-overline text-rose-600 mb-3">
            <WarningCircle size={14} weight="bold" /> Something went wrong on this page
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
            We caught it before it broke the platform.
          </h1>
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed max-w-md">
            Your session, workspaces and saved work are untouched. The rest of Orixa Invest is still
            running. Use the actions below to recover, or try a different module.
          </p>
          <div className="mt-4 text-xs font-mono text-muted-foreground bg-secondary border border-border rounded-lg px-3 py-2 break-words">
            {errMsg.slice(0, 280)}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
              data-testid="error-boundary-retry"
            >
              <ArrowsClockwise size={14} weight="bold" /> Try again
            </button>
            <button
              onClick={this.handleHardReload}
              className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors"
              data-testid="error-boundary-reload"
            >
              Hard reload
            </button>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-2"
              data-testid="error-boundary-dashboard"
            >
              <House size={14} weight="bold" /> Back to dashboard
            </Link>
          </div>
          <div className="mt-10 text-xs text-muted-foreground/80">
            If the problem persists, contact{" "}
            <a href="mailto:support@orixa-invest.co" className="underline hover:text-foreground">
              support@orixa-invest.co
            </a>{" "}
            with the timestamp and your last action.
          </div>
        </div>
      </div>
    );
  }
}
